/**
 * 门派系统引擎
 */

import type { PlayerState } from './Player';
import type { SectMission } from '../data/sectMissions';
import { generateMissions, generateMandatoryMission, SECT_SHOP, type SectShopItem } from '../data/sectMissions';
import { ITEMS } from '../data/items';

/** 判断是否为门派弟子 */
export function isSectDisciple(player: PlayerState): boolean {
  return player.identityId?.startsWith('sect_');
}

/** 获取或刷新任务列表 */
export function getMissions(player: PlayerState): SectMission[] {
  if (!player.sectMissions || player.sectMissions.length === 0) {
    player.sectMissions = generateMissions(player);
  }
  return player.sectMissions;
}

/** 获取任务并顺便检查修炼任务（打开面板时用） */
export function getMissionsAndCheckRealm(player: PlayerState): SectMission[] {
  const missions = getMissions(player);
  // 只在这里做一次检查，不会循环因为 checkRealmMissions 不调 getMissions
  checkRealmMissionsSilent(player);
  return missions;
}

function checkRealmMissionsSilent(player: PlayerState): void {
  if (!isSectDisciple(player)) return;
  const missions = player.sectMissions;
  if (!missions || missions.length === 0) return;

  for (const m of missions) {
    if (m.type !== 'reach_realm' || (m as any)._completed) continue;
    if (player.realmKey.startsWith(m.realmPrefix!)) {
      player.sectContribution = (player.sectContribution ?? 0) + m.contributionReward;
      player.spiritStones += m.stoneReward;
      (m as any)._completed = true;
    }
  }
  player.sectMissions = player.sectMissions.filter((m) => !(m as any)._completed);
}

/** 检查并自动完成进度类任务 */
export function checkMissionProgress(player: PlayerState, type: string, amount: number): string[] {
  if (!isSectDisciple(player)) return [];
  const missions = getMissions(player);
  const msgs: string[] = [];

  for (const m of missions) {
    if (m.type === type) {
      m.progress = (m.progress ?? 0) + amount;
      if (m.progress >= m.target && !(m as any)._completed) {
        (m as any)._completed = true;
        player.sectContribution = (player.sectContribution ?? 0) + m.contributionReward;
        player.spiritStones += m.stoneReward;
        msgs.push(`✅ 宗门任务「${m.name}」完成！+${m.contributionReward}贡献 +${m.stoneReward}灵石`);
      }
    }
  }

  // 清除已完成任务
  player.sectMissions = player.sectMissions.filter((m) => !(m as any)._completed);
  return msgs;
}

/** 提交物品类任务 */
export function submitItemsForMission(player: PlayerState): string[] {
  if (!isSectDisciple(player)) return [];
  const missions = getMissions(player);
  const msgs: string[] = [];

  for (const m of missions) {
    if (m.type !== 'submit_items' || (m as any)._completed) continue;
    let submitted = 0;
    while (submitted < m.target) {
      const idx = player.inventory.findIndex((i) => i.id === m.itemId);
      if (idx === -1) break;
      player.inventory.splice(idx, 1);
      submitted++;
    }
    if (submitted > 0) {
      player.sectContribution = (player.sectContribution ?? 0) + m.contributionReward;
      player.spiritStones += m.stoneReward;
      (m as any)._completed = true;
      msgs.push(`✅ 宗门任务「${m.name}」完成！+${m.contributionReward}贡献 +${m.stoneReward}灵石`);
    }
  }

  player.sectMissions = player.sectMissions.filter((m) => !(m as any)._completed);
  return msgs;
}

/** 兑换贡献商店物品 */
export function redeemShopItem(player: PlayerState, itemId: string): { ok: boolean; message: string } {
  const item = SECT_SHOP.find((i) => i.id === itemId);
  if (!item) return { ok: false, message: '商品不存在' };

  const contrib = player.sectContribution ?? 0;
  if (contrib < item.cost) {
    return { ok: false, message: `贡献不足！需要${item.cost}，当前${contrib}` };
  }

  player.sectContribution = contrib - item.cost;

  if (item.stoneAmount) {
    player.spiritStones += item.stoneAmount;
    return { ok: true, message: `兑换成功！获得 ${item.stoneAmount} 灵石` };
  }

  if (item.resultItemId) {
    const template = ITEMS.find((i) => i.id === item.resultItemId);
    if (template) {
      for (let i = 0; i < (item.resultCount ?? 1); i++) {
        player.inventory.push(JSON.parse(JSON.stringify(template)));
      }
    }
    return { ok: true, message: `兑换成功！获得 ${template?.name ?? item.resultItemId}×${item.resultCount ?? 1}` };
  }

  return { ok: false, message: '兑换失败' };
}

/** 检查修炼类任务（突破时调用） */
export function checkRealmMissions(player: PlayerState): string[] {
  if (!isSectDisciple(player)) return [];
  const missions = getMissions(player);
  const msgs: string[] = [];

  for (const m of missions) {
    if (m.type !== 'reach_realm' || (m as any)._completed) continue;
    if (player.realmKey.startsWith(m.realmPrefix!)) {
      player.sectContribution = (player.sectContribution ?? 0) + m.contributionReward;
      player.spiritStones += m.stoneReward;
      (m as any)._completed = true;
      msgs.push(`✅ 宗门任务「${m.name}」完成！+${m.contributionReward}贡献 +${m.stoneReward}灵石`);
    }
  }

  player.sectMissions = player.sectMissions.filter((m) => !(m as any)._completed);
  return msgs;
}

/** 刷新任务（每日） */
export function refreshMissions(player: PlayerState): SectMission[] {
  player.sectMissions = generateMissions(player);
  return player.sectMissions;
}

const MANDATORY_CD_MS = 12 * 3600 * 1000; // 12小时

/** 检查并尝试提交强制任务 */
export function checkMandatoryMission(player: PlayerState): string | null {
  if (!isSectDisciple(player)) return null;

  const now = Date.now();
  const doneAt = player.mandatoryMissionDoneAt ?? 0;
  const cdRemaining = MANDATORY_CD_MS - (now - doneAt);

  // 在CD内 → 修炼正常，清除残留任务对象
  if (doneAt > 0 && cdRemaining > 0) {
    if (player.mandatoryMission) player.mandatoryMission = null;
    return null;
  }

  // CD已过 → 清除旧任务，分配新任务
  if (doneAt > 0 && cdRemaining <= 0) {
    player.mandatoryMission = null;
    player.mandatoryMissionDoneAt = 0;
  }

  // 没有强制任务 → 分配新任务
  if (!player.mandatoryMission) {
    player.mandatoryMission = generateMandatoryMission(player);
    return `⚠️ 宗门指派了新差事：${player.mandatoryMission.desc}`;
  }

  // 尝试提交材料：先检查数量是否足够
  const m = player.mandatoryMission;
  const owned = player.inventory.filter((i: any) => i.id === m.itemId).length;
  if (owned < m.target) {
    return `⚠️ 材料不足！需要 ${m.itemName}×${m.target}，当前只有 ${owned} 个。修炼仍然冻结中。`;
  }

  // 足够则提交
  for (let i = 0; i < m.target; i++) {
    const idx = player.inventory.findIndex((item: any) => item.id === m.itemId);
    if (idx !== -1) player.inventory.splice(idx, 1);
  }
  player.sectContribution = (player.sectContribution ?? 0) + m.contributionReward;
  player.spiritStones += m.stoneReward;
  player.mandatoryMission = null;
  player.mandatoryMissionDoneAt = now;
  return `✅ 强制任务完成！获得${m.contributionReward}贡献 +${m.stoneReward}灵石。修炼已恢复，12小时后宗门将发布新任务。`;
}
